<template>
  <div class="bg-white rounded-2xl shadow-xl border border-gray-100 p-6">
    <h3 class="text-lg font-bold text-gray-900">{{ t("title") }}</h3>
    <p class="mt-0.5 text-sm text-gray-500">{{ t(`subtitle_${challenge.countingMethod}`) }}</p>

    <div class="flex gap-4 mt-1 text-xs text-gray-500">
      <span class="flex items-center gap-1.5">
        <span class="inline-block w-2.5 h-2.5 rounded-xs bg-primary"></span>
        {{ t("legend_actual") }}
      </span>
      <span v-if="weeklyTarget > 0" class="flex items-center gap-1.5">
        <span class="inline-block w-2.5 h-2.5 rounded-xs bg-gray-200"></span>
        {{ t("legend_target") }}
      </span>
    </div>

    <div :style="chartGridStyle">
      <div v-for="week in allWeeks" :key="week.label" class="flex flex-col items-center justify-end h-full gap-2">
        <div class="text-xs font-bold" :class="week.isPast ? 'text-gray-900' : 'text-gray-300'">
          {{ week.isPast ? week.count : "—" }}
        </div>
        <div class="flex items-end w-full h-full justify-center gap-1">
          <ChartBar v-if="weeklyTarget > 0" variant="target" :height="targetBarHeight + '%'" />
          <ChartBar :variant="week.isPast ? 'solid' : 'projected'" :height="actualBarHeight(week)" />
        </div>
        <div class="text-xs text-gray-500">{{ week.label }}</div>
      </div>
    </div>
  </div>
</template>

<script>
import { useI18n } from "vue-i18n";
import { ChartBar, parseFrDate } from "@estaly/ui";

const WEEKS_PER_MONTH = 30 / 7;

export default {
  components: { ChartBar },

  setup() {
    const { t } = useI18n();
    return { t };
  },

  props: {
    weeklyData: {
      type: Array,
      default: () => [],
    },
    challenge: {
      type: Object,
      required: true,
    },
    stores: {
      type: Array,
      default: () => [],
    },
  },

  computed: {
    weeklyTarget() {
      const monthlyTotal = this.stores.reduce((sum, s) => sum + (s.monthlySalesTarget || 0), 0);
      if (!monthlyTotal) return 0;
      return Math.round(monthlyTotal / WEEKS_PER_MONTH);
    },

    allWeeks() {
      const start = parseFrDate(this.challenge.startDate);
      const end = parseFrDate(this.challenge.endDate);
      const now = new Date();

      const dataMap = {};
      this.weeklyData.forEach((d) => {
        dataMap[d.weekStart] = d.count;
      });

      const weeks = [];
      const current = new Date(start);
      const dayOfWeek = current.getDay();
      const mondayOffset = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
      current.setDate(current.getDate() + mondayOffset);

      while (current <= end) {
        const weekStart = this.formatISO(current);
        const isoWeek = this.getISOWeek(current);
        const weekEnd = new Date(current);
        weekEnd.setDate(weekEnd.getDate() + 6);
        const isPast = weekEnd < now;

        weeks.push({
          label: `S${isoWeek}`,
          count: dataMap[weekStart] || 0,
          isPast,
        });

        current.setDate(current.getDate() + 7);
      }

      return weeks;
    },

    avgPastCount() {
      const past = this.allWeeks.filter((w) => w.isPast && w.count > 0);
      if (!past.length) return 0;
      return Math.round(past.reduce((s, w) => s + w.count, 0) / past.length);
    },

    effectiveMax() {
      const maxActual = Math.max(0, ...this.allWeeks.map((w) => w.count));
      return Math.max(maxActual, this.avgPastCount, this.weeklyTarget, 1);
    },

    targetBarHeight() {
      if (!this.weeklyTarget) return 0;
      return Math.max(5, Math.round((this.weeklyTarget / this.effectiveMax) * 100));
    },

    chartGridStyle() {
      return {
        display: "grid",
        gridTemplateColumns: `repeat(${this.allWeeks.length}, 1fr)`,
        gap: "14px",
        alignItems: "end",
        height: "220px",
        marginTop: "16px",
      };
    },
  },

  methods: {
    actualBarHeight(week) {
      const count = week.isPast ? week.count : this.avgPastCount;
      if (!count) return "0%";
      return Math.max(5, Math.round((count / this.effectiveMax) * 100)) + "%";
    },

    formatISO(date) {
      const y = date.getFullYear();
      const m = String(date.getMonth() + 1).padStart(2, "0");
      const d = String(date.getDate()).padStart(2, "0");
      return `${y}-${m}-${d}`;
    },

    getISOWeek(date) {
      const d = new Date(date);
      d.setHours(0, 0, 0, 0);
      d.setDate(d.getDate() + 3 - ((d.getDay() + 6) % 7));
      const week1 = new Date(d.getFullYear(), 0, 4);
      return 1 + Math.round(((d - week1) / 86400000 - 3 + ((week1.getDay() + 6) % 7)) / 7);
    },
  },
};
</script>

<i18n src="./TieredChallengeWeeklyChart.i18n.json"></i18n>
